// OIDC redirect-back handler. react-oidc-context's onSigninCallback
// strips the ?code/?state params; once the AuthProvider finishes
// processing, restore the requested internal route.

import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";

import { getReturnTo } from "@/auth/return-to";

export function CallbackPage(): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect((): void => {
    if (!auth.isLoading && auth.isAuthenticated && !auth.error) {
      void navigate(getReturnTo(auth.user?.state), { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.user?.state, navigate]);

  // Safety net — if an operator lands on /callback without OIDC
  // ?code/?state (bookmark, stale link, manual URL), react-oidc-
  // context never fires signinCallback and isLoading hangs true.
  // Bounce home after 5s so the operator isn't stuck on
  // "Completing sign-in…" forever.
  useEffect((): (() => void) => {
    const timer = setTimeout((): void => {
      if (!auth.isAuthenticated && !auth.error) {
        void navigate("/", { replace: true });
      }
    }, 5_000);
    return (): void => clearTimeout(timer);
  }, [auth.isAuthenticated, auth.error, navigate]);

  if (auth.error) {
    return (
      <div className="p-6 text-sm text-[var(--state-danger)]">
        {t("auth.callback_error")} <span className="font-mono">{auth.error.message}</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-sm text-[var(--color-muted-foreground)]">
      {t("auth.callback_processing")}
    </div>
  );
}
